import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@papier/ui/components/ui/card";
import { Skeleton } from "@papier/ui/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@papier/ui/components/ui/table";
import { cn } from "@papier/ui/lib/utils";
import { Loader2 } from "lucide-react";

export function PendingProjectsComponent() {
  return (
    <div className="flex h-[40vh] items-center justify-center">
      <p className="text-white">Loading org projects...</p>
    </div>
  );
}

export function PendingTemplatesComponent() {
  return (
    <section className="grid gap-8">
      <section className="grid gap-3">
        <Skeleton className="w-50 h-7" />
        <div className="grid h-[20vh] grid-cols-1 items-center justify-center gap-6 md:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="size-full" />
          ))}
        </div>
      </section>
      <section className="grid gap-3">
        <Skeleton className="w-50 h-7" />
        <div className="grid h-[20vh] grid-cols-1 items-center justify-center gap-6 md:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="size-full" />
          ))}
        </div>
      </section>
    </section>
  );
}

export function PendingProjectComponent() {
  return (
    <div className="flex h-[40vh] items-center justify-center">
      <p className="text-white">Loading project...</p>
    </div>
  );
}

export function PendingTemplateComponent() {
  return (
    <div className="flex h-[40vh] items-center justify-center">
      <p className="text-white">Loading template...</p>
    </div>
  );
}
export function PendingOrganizationComponent() {
  return (
    <div className="flex h-[40vh] items-center justify-center">
      <p className="text-white">Loading org settings...</p>
    </div>
  );
}

export function PendingApiKeysComponent() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="md:h-5.5 my-0.5 h-5 w-1/3" />
        <Skeleton className="mb-0.5 mt-1.5 h-3 w-2/3 md:h-3.5" />
      </CardHeader>
      <CardContent className="h-40 p-6"></CardContent>
    </Card>
  );
}

export function PendingBillingComponent() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="md:h-5.5 my-0.5 h-5 w-1/3" />
        <Skeleton className="mb-0.5 mt-1.5 h-3 w-2/3 md:h-3.5" />
      </CardHeader>
      <CardContent className="h-50 p-6">
        <Skeleton className="size-full" />
      </CardContent>

      <CardFooter className="border-t">
        <Skeleton className="w-30 h-8 md:ms-auto" />
      </CardFooter>
    </Card>
  );
}

export function PendingSuccessComponent() {
  return (
    <div className="flex h-[80svh] items-center justify-center px-4 py-12">
      <Card className="border-border w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <Loader2 className="mx-auto mb-4 size-10 animate-spin rounded-full" />
          <CardTitle className="text-xl">Processing Your Subscription</CardTitle>
          <CardDescription>Please wait while we set up your account...</CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
}

export function PendingUsersComponent() {
  return (
    <div className="border-muted overflow-hidden rounded-lg border-2">
      <Table>
        <TableHeader className="bg-secondary sticky top-0">
          <TableRow>
            {[
              { label: "User" },
              { label: "Status" },
              { label: "Verification" },
              { label: "Linked Accounts" },
              { label: "Role" },
              { label: "Last Sign In" },
              { label: "Created" },
              { label: "Actions", className: "w-[80px]" },
            ].map((col) => {
              return (
                <TableHead
                  key={col.label}
                  className={cn("text-secondary-foreground px-3", col.className)}
                >
                  {col.label}
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 5 }).map((_, index) => (
            <TableRow key={index}>
              <TableCell className="p-3">
                <div className="flex items-center gap-4">
                  <Skeleton className="size-8 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-[120px]" />
                    <Skeleton className="h-3 w-[160px]" />
                  </div>
                </div>
              </TableCell>
              <TableCell className="p-3">
                <Skeleton className="h-6 w-[140px]" />
              </TableCell>
              <TableCell className="p-3">
                <div className="flex">
                  <Skeleton className="size-8 rounded-full" />
                  <Skeleton className="size-8 rounded-full" />
                </div>
              </TableCell>
              <TableCell className="p-3">
                <Skeleton className="h-6 w-[140px]" />
              </TableCell>
              <TableCell className="p-3">
                <Skeleton className="h-6 w-[100px]" />
              </TableCell>
              <TableCell className="p-3">
                <Skeleton className="h-4 w-[140px]" />
              </TableCell>
              <TableCell className="p-3">
                <Skeleton className="h-4 w-[140px]" />
              </TableCell>
              <TableCell className="p-3">
                <Skeleton className="size-8 rounded-md" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function PendingOrgsComponent() {
  return (
    <div className="border-muted overflow-hidden rounded-lg border-2">
      <Table>
        <TableHeader className="bg-secondary sticky top-0">
          <TableRow>
            {[
              { label: "Organization" },
              { label: "Plan" },
              { label: "Members" },
              { label: "Created" },
              { label: "Actions", className: "w-[80px]" },
            ].map((col) => {
              return (
                <TableHead
                  key={col.label}
                  className={cn("text-secondary-foreground px-3", col.className)}
                >
                  {col.label}
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 5 }).map((_, index) => (
            <TableRow key={index}>
              <TableCell className="p-3">
                <div className="flex items-center gap-4">
                  <Skeleton className="size-8 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-[120px]" />
                    <Skeleton className="h-3 w-[120px]" />
                  </div>
                </div>
              </TableCell>
              <TableCell className="p-3">
                <div className="flex gap-1">
                  <Skeleton className="h-6 w-10" />
                  <Skeleton className="h-6 w-10" />
                </div>
              </TableCell>
              <TableCell className="p-3">
                <Skeleton className="size-8 w-20 rounded-full" />
              </TableCell>
              <TableCell className="p-3">
                <Skeleton className="h-4 w-[140px]" />
              </TableCell>
              <TableCell className="p-3">
                <Skeleton className="size-8 rounded-md" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
